import React, { useCallback, useEffect, useRef, useState } from 'react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyC36o4niEA5vZG4bMS0CM5FBQaKEt6QADw';
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;
const SELECTED_ZOOM = 15;

let googleMapsLoader;

const loadGoogleMaps = () => {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsLoader) return googleMapsLoader;

  googleMapsLoader = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-routes-google-maps="true"]');

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google.maps), { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.defer = true;
    script.dataset.routesGoogleMaps = 'true';
    script.onload = () => resolve(window.google.maps);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return googleMapsLoader;
};

const parseCoordinate = (value, min, max) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : null;
};

const formatCoordinate = (value) => Number(value).toFixed(6);

export default function AccidentLocationMap({
  address,
  latitude,
  longitude,
  onCoordinatesChange,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const lastGeocodedAddressRef = useRef('');
  const [status, setStatus] = useState('Loading map...');

  const updateMarker = useCallback((maps, position, shouldNotify = false) => {
    if (!mapRef.current) return;

    if (!markerRef.current) {
      markerRef.current = new maps.Marker({
        map: mapRef.current,
        draggable: true,
        title: 'Accident location',
      });

      markerRef.current.addListener('dragend', () => {
        const markerPosition = markerRef.current.getPosition();
        if (!markerPosition) return;
        onCoordinatesChange({
          latitude: formatCoordinate(markerPosition.lat()),
          longitude: formatCoordinate(markerPosition.lng()),
        });
      });
    }

    markerRef.current.setPosition(position);
    mapRef.current.setCenter(position);
    mapRef.current.setZoom(SELECTED_ZOOM);
    setStatus('');

    if (shouldNotify) {
      onCoordinatesChange({
        latitude: formatCoordinate(position.lat),
        longitude: formatCoordinate(position.lng),
      });
    }
  }, [onCoordinatesChange]);

  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;

        if (!mapRef.current) {
          mapRef.current = new maps.Map(containerRef.current, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          });
          geocoderRef.current = new maps.Geocoder();

          mapRef.current.addListener('click', (event) => {
            if (!event.latLng) return;
            updateMarker(maps, { lat: event.latLng.lat(), lng: event.latLng.lng() }, true);
          });
        }

        setStatus('');
      })
      .catch(() => {
        if (!cancelled) setStatus('Map could not be loaded.');
      });

    return () => {
      cancelled = true;
    };
  }, [updateMarker]);

  useEffect(() => {
    loadGoogleMaps().then((maps) => {
      const lat = parseCoordinate(latitude, -90, 90);
      const lng = parseCoordinate(longitude, -180, 180);

      if (lat === null || lng === null) return;
      updateMarker(maps, { lat, lng });
    }).catch(() => {});
  }, [latitude, longitude, updateMarker]);

  useEffect(() => {
    const trimmedAddress = String(address || '').trim();
    if (trimmedAddress.length < 6 || trimmedAddress === lastGeocodedAddressRef.current) return;

    const timeoutId = window.setTimeout(() => {
      loadGoogleMaps().then((maps) => {
        if (!geocoderRef.current) return;

        geocoderRef.current.geocode({ address: trimmedAddress }, (results, geocoderStatus) => {
          if (geocoderStatus !== 'OK' || !results?.[0]?.geometry?.location) {
            setStatus('Enter a complete address or coordinates to show the pin.');
            return;
          }

          lastGeocodedAddressRef.current = trimmedAddress;
          const location = results[0].geometry.location;
          updateMarker(maps, { lat: location.lat(), lng: location.lng() }, true);
        });
      }).catch(() => {});
    }, 600);

    return () => window.clearTimeout(timeoutId);
  }, [address, updateMarker]);

  return (
    <div className="open-form-map-wrap">
      <div ref={containerRef} className="open-form-map" />
      {status ? <div className="open-form-map-status">{status}</div> : null}
    </div>
  );
}
